import { Button, ButtonIcon } from '@/components/ui/button'
import { HStack } from '@/components/ui/hstack'
import { VStack } from '@/components/ui/vstack'
import UploadFile, { UploadFileRef } from '@/components/UploadFile'
import UploadPrefix from '@/components/UploadPrefix'
import { UploadFileAsset } from '@/lib/api/qiniuApi'
import { cn } from '@/lib/cn'
import usePlatformColor from '@/lib/hooks/usePlatformColor'
import {
  MessagePart,
  removeAllUserInputAttachments,
  selectConversation,
  selectIsLastMessageGenerating,
  setInterrupted,
  setUserInputAttachments,
  setUserInputContent,
  upsertMessage,
} from '@/redux/slices/aiChatSlice'
import { useAppDispatch, useAppSelector } from '@/redux/store'
import { useActionSheet } from '@expo/react-native-action-sheet'
import { nanoid } from '@reduxjs/toolkit'
import { getDocumentAsync } from 'expo-document-picker'
import {
  CameraType,
  launchCameraAsync,
  launchImageLibraryAsync,
  PermissionStatus,
  requestCameraPermissionsAsync,
  requestMediaLibraryPermissionsAsync,
} from 'expo-image-picker'
import { ArrowUpIcon, CircleDotIcon, PaperclipIcon } from 'lucide-react-native'
import { useMemo, useRef, useState } from 'react'
import { Platform, Text, TextInput, TextStyle } from 'react-native'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import AttachmentItem from './AttachmentItem'

type UserInputBoxProps = {
  className?: string
  conversationId: string
  disabled?: boolean
}

const INPUT_BOX_TEXT_STYLE: TextStyle = {
  fontSize: 16,
  lineHeight: 20,
  paddingTop: 0,
  paddingBottom: 0,
}

const INPUT_BOX_MIN_ROWS = 2
const INPUT_BOX_MAX_ROWS = 5

const INPUT_BOX_MIN_HEIGHT = INPUT_BOX_TEXT_STYLE.lineHeight! * INPUT_BOX_MIN_ROWS
const INPUT_BOX_MAX_HEIGHT = INPUT_BOX_TEXT_STYLE.lineHeight! * INPUT_BOX_MAX_ROWS

const INPUT_BOX_EXTRA_PADDING_BLOCK = 4

const MAX_ATTACHMENTS_COUNT = 3

export default function UserInputBox({ className, conversationId, disabled }: UserInputBoxProps) {
  const dispatch = useAppDispatch()
  const inputRef = useRef<TextInput>(null)
  const [textHeight, setTextHeight] = useState(0)
  const [placeholderColor, labelColor] = usePlatformColor('tertiaryLabel', 'label')

  const userInputContent = useAppSelector(state => selectConversation(state.aiChat, conversationId)?.userInput.content)
  const userInputAttachments = useAppSelector(
    state => selectConversation(state.aiChat, conversationId)?.userInput.attachments
  )

  const uploadRef = useRef<UploadFileRef>(null)

  const isAIReplying = useAppSelector(state => selectIsLastMessageGenerating(state, conversationId))

  const sendDisabled = useMemo(
    () => disabled || (!userInputContent?.trim() && !userInputAttachments?.length),
    [disabled, userInputAttachments, userInputContent]
  )

  const handleTextInputChange = (content: string) => {
    dispatch(setUserInputContent({ conversationId, content }))
  }

  const handleSend = () => {
    if (isAIReplying || sendDisabled) return

    inputRef.current?.blur()

    const chunks: MessagePart[] =
      userInputAttachments?.map(attachment => ({ key: attachment.id, type: 'attachment', attachment })) ?? []
    if (userInputContent?.trim()) chunks.push({ type: 'text', content: userInputContent })

    dispatch(upsertMessage({ conversationId, message: { id: nanoid(), chunks, role: 'user', done: true } }))
    dispatch(setUserInputContent({ conversationId, content: '' }))
    dispatch(removeAllUserInputAttachments({ conversationId }))
    uploadRef.current?.removeAllUploadItems()
  }

  const handleStop = () => {
    if (!isAIReplying) return

    dispatch(setInterrupted({ conversationId }))
  }

  return (
    <VStack
      className={cn(
        'relative p-3 bg-background-0 h-auto border border-gray-100 mx-4 rounded-lg shadow-[0_3px_10px_4px_#0000000d]',
        className
      )}
    >
      <UploadFile
        ref={uploadRef}
        onSuccess={attachments => dispatch(setUserInputAttachments({ conversationId, attachments }))}
      >
        {({ uploadItems, uploadFile, removeUploadItem }) => (
          <>
            <HStack space="sm" className="mb-1">
              {uploadItems.map(item => (
                <AttachmentItem
                  key={item.id}
                  attachment={item.status === 'success' ? item.attachment : undefined}
                  uploadFileAsset={item.file}
                  uploadProgress={item.status === 'uploading' ? item.progress : undefined}
                  onRemove={() => removeUploadItem(item.id)}
                />
              ))}
            </HStack>

            <TextInput
              ref={inputRef}
              multiline
              style={{
                ...INPUT_BOX_TEXT_STYLE,
                color: labelColor,
                height:
                  Math.max(INPUT_BOX_MIN_HEIGHT, Math.min(textHeight, INPUT_BOX_MAX_HEIGHT)) +
                  INPUT_BOX_EXTRA_PADDING_BLOCK,
                outline: 'none',
              }}
              placeholder="告诉我你想要什么表单"
              placeholderTextColor={placeholderColor}
              value={userInputContent}
              onChangeText={handleTextInputChange}
              editable={!disabled}
              returnKeyType="send"
              submitBehavior="submit"
              {...(Platform.OS === 'web' && { blurOnSubmit: true })}
              onSubmitEditing={handleSend}
            />

            <HStack space="sm">
              <HStack className="flex-1">
                {uploadItems.length < MAX_ATTACHMENTS_COUNT && <AttachmentUploader uploadFile={uploadFile} />}
              </HStack>

              <Button
                className="w-10 h-10 rounded-md disabled:opacity-50 px-0"
                action="primary"
                onPress={isAIReplying ? handleStop : handleSend}
                disabled={isAIReplying ? false : sendDisabled || uploadItems.some(item => item.status === 'uploading')}
              >
                <ButtonIcon as={isAIReplying ? CircleDotIcon : ArrowUpIcon} size={20} className="text-typography-0" />
              </Button>
            </HStack>
          </>
        )}
      </UploadFile>

      {/* https://github.com/facebook/react-native/issues/52854 */}
      <Text
        className="absolute opacity-0 pointer-events-none"
        style={INPUT_BOX_TEXT_STYLE}
        onLayout={e => setTextHeight(e.nativeEvent.layout.height)}
      >
        {userInputContent}
      </Text>
    </VStack>
  )
}

const SUPPORTED_TYPES = [
  'image/jpeg',
  'image/png',
  'image/bmp',
  'image/webp',
  'image/heic',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'application/pdf',
]

const getUploadPrefix = (file: UploadFileAsset): UploadPrefix => {
  if (!file.mimeType) return UploadPrefix.AIGenerationImage
  return file.mimeType.startsWith('image/') ? UploadPrefix.AIGenerationImage : UploadPrefix.AIGenerationDocument
}

type AttachmentUploaderProps = {
  uploadFile: (file: UploadFileAsset, prefix: UploadPrefix) => void
}

function AttachmentUploader({ uploadFile }: AttachmentUploaderProps) {
  const insets = useSafeAreaInsets()
  const { showActionSheetWithOptions } = useActionSheet()

  const handleTakePhoto = async () => {
    try {
      const result = await requestCameraPermissionsAsync()
      if (result.status !== PermissionStatus.GRANTED) return
    } catch {}
    const result = await launchCameraAsync({ base64: false, cameraType: CameraType.back })
    if (result.canceled) return

    console.log('from camera', result.assets)
    result.assets.forEach(asset => uploadFile(asset, getUploadPrefix(asset)))
  }

  const handleSelectFromAlbum = async () => {
    try {
      const result = await requestMediaLibraryPermissionsAsync()
      if (result.status !== PermissionStatus.GRANTED) return
    } catch {
      return
    }
    const result = await launchImageLibraryAsync({ base64: false })
    if (result.canceled) return

    console.log('from album', result.assets)
    result.assets.forEach(asset => uploadFile(asset, getUploadPrefix(asset)))
  }

  const handleSelectFromFiles = async () => {
    const result = await getDocumentAsync({ type: SUPPORTED_TYPES, base64: false })
    if (result.canceled) return

    console.log('from files', result.assets)
    result.assets.forEach(asset => uploadFile(asset, getUploadPrefix(asset)))
  }

  const handleSelect = () => {
    if (Platform.OS === 'web') {
      handleSelectFromFiles()
      return
    }

    const options = ['拍照', '从相册选择', '从文件选择', '取消']
    const cancelButtonIndex = options.length - 1
    showActionSheetWithOptions(
      {
        options,
        cancelButtonIndex,
        containerStyle: Platform.OS === 'android' ? { paddingBottom: insets.bottom } : undefined,
      },
      index => {
        if (index === cancelButtonIndex) return
        if (index === 0) handleTakePhoto()
        else if (index === 1) handleSelectFromAlbum()
        else if (index === 2) handleSelectFromFiles()
      }
    )
  }

  return (
    <Button onPress={handleSelect} action="default" className="w-10 h-10 rounded-md data-[active=true]:opacity-50 px-0">
      <ButtonIcon as={PaperclipIcon} />
    </Button>
  )
}
